(()=>{
  const ROOT_SELECTOR='[data-it-native-settings]';
  const pickerSrc=window.__IT_NATIVE_PICKER_SRC__||'';
  let detachedRoot=null;
  let placeholder=null;
  let pickerLoaded=document.documentElement.dataset.itRingRoute!=='nivoda';
  let mediaRoot=null;
  const cache=new Map();

  const route=()=>document.documentElement.dataset.itRingRoute||'native';
  const canonical=url=>{
    if(!url)return'';
    try{
      const u=new URL(url,location.origin);
      return u.pathname.replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|\d+x\d*|\d+x)(?=\.[a-z0-9]+$)/i,'');
    }catch(e){return String(url).split(/[?#]/)[0];}
  };
  const productFor=card=>{
    const url=card?.dataset.productJson;
    if(!url)return Promise.reject(new Error('Missing product JSON'));
    if(!cache.has(url)){
      cache.set(url,fetch(url,{headers:{Accept:'application/json'}}).then(r=>{
        if(!r.ok)throw new Error(`Product data request failed (${r.status})`);
        return r.json();
      }).catch(error=>{cache.delete(url);throw error;}));
    }
    return cache.get(url);
  };
  const optionIndex=(product,wanted)=>(product?.options||[]).findIndex(option=>{
    const name=String(typeof option==='string'?option:option?.name||'').trim().toLowerCase();
    return wanted==='metal'?name.includes('metal'):wanted==='shape'?name.includes('shape'):name===wanted;
  });
  const optionValue=(variant,index)=>index<0||!variant?'':String(Array.isArray(variant.options)?variant.options[index]||'':variant[`option${index+1}`]||'');
  const variantImage=variant=>typeof variant?.featured_image==='string'?variant.featured_image:String(variant?.featured_image?.src||variant?.featured_image?.url||'');
  const variantAlt=variant=>String(variant?.featured_image?.alt||'').trim();
  const neutral=card=>`${card?.dataset.productTitle||'Engagement ring'} engagement ring setting`;
  const descriptiveAlt=(card,product,variant)=>{
    const supplied=variantAlt(variant);
    if(supplied)return supplied;
    const metalIndex=optionIndex(product,'metal');
    const shapeIndex=optionIndex(product,'shape');
    const metal=optionValue(variant,metalIndex).trim();
    const shape=optionValue(variant,shapeIndex).trim();
    const detail=[metal,shape?`${shape} centre compatible`:''].filter(Boolean).join(' · ');
    return detail?`${neutral(card)} — ${detail}`:neutral(card);
  };
  const matchingVariant=(product,img)=>{
    const current=canonical(img?.currentSrc||img?.src||'');
    if(!current)return null;
    return (product?.variants||[]).find(variant=>canonical(variantImage(variant))===current)||null;
  };
  const setAlt=(img,value)=>{
    if(img&&value&&img.alt!==value)img.alt=value;
  };
  const metalDatasetKey=value=>({
    'white-gold':'metalImageWhiteGold',
    'yellow-gold':'metalImageYellowGold',
    'rose-gold':'metalImageRoseGold',
    'platinum':'metalImagePlatinum'
  }[value]||'');
  const reconcileCard=async(card,img)=>{
    if(!card||!img)return;
    const title=card.dataset.productTitle||'Engagement ring';
    const topMetal=card.closest(ROOT_SELECTOR)?.querySelector('[data-it-filter-metal]')?.value||'';
    const topShape=card.closest(ROOT_SELECTOR)?.querySelector('[data-it-filter-shape]')?.value||'';
    if(!topMetal&&!topShape){
      setAlt(img,`${title} engagement ring setting`);
      return;
    }
    if(topMetal&&!topShape){
      const key=metalDatasetKey(topMetal);
      const mapped=key?card.dataset[key]||'':'';
      if(mapped&&canonical(mapped)===canonical(img.currentSrc||img.src)){
        const label=card.closest(ROOT_SELECTOR)?.querySelector('[data-it-filter-metal]')?.selectedOptions?.[0]?.textContent?.trim()||'';
        setAlt(img,label?`${title} engagement ring setting — ${label}`:`${title} engagement ring setting`);
      }else{
        setAlt(img,`${title} engagement ring setting`);
      }
      return;
    }
    try{
      const product=await productFor(card);
      const variant=matchingVariant(product,img);
      setAlt(img,variant?descriptiveAlt(card,product,variant):neutral(card));
    }catch(e){
      setAlt(img,neutral(card));
    }
  };
  const reconcileSelected=async(root,img)=>{
    if(!root||!img||!img.getAttribute('src'))return;
    const card=root.querySelector('[data-it-setting-card].is-selected');
    if(!card){setAlt(img,'Selected engagement ring setting');return;}
    try{
      const product=await productFor(card);
      const variant=matchingVariant(product,img);
      setAlt(img,variant?descriptiveAlt(card,product,variant):neutral(card));
    }catch(e){
      setAlt(img,neutral(card));
    }
  };
  const reconcileImage=img=>{
    if(!img)return;
    const card=img.closest('[data-it-setting-card]');
    if(card){reconcileCard(card,img);return;}
    const root=img.closest(ROOT_SELECTOR);
    if(root&&img.matches('[data-it-selected-image]'))reconcileSelected(root,img);
  };
  const startMediaTruth=root=>{
    if(!root||mediaRoot===root)return;
    mediaRoot=root;
    root.querySelectorAll('[data-it-setting-card] .it-native-setting-card__media img').forEach(img=>{
      const card=img.closest('[data-it-setting-card]');
      if((img.alt||'').endsWith(' engagement ring setting top view'))setAlt(img,neutral(card));
    });
    const observer=new MutationObserver(records=>{
      const images=new Set();
      records.forEach(record=>{
        if(record.target instanceof HTMLImageElement)images.add(record.target);
      });
      images.forEach(reconcileImage);
    });
    observer.observe(root,{subtree:true,attributes:true,attributeFilter:['src','srcset','alt']});
  };
  const remediateA11y=root=>{
    if(!root)return;
    root.dataset.itA11yRemediated='true';
    const nativeMetal=root.querySelector('[data-it-filter-metal]');
    const nativeShape=root.querySelector('[data-it-filter-shape]');
    [nativeMetal,nativeShape].forEach(control=>{
      if(!control)return;
      control.setAttribute('aria-hidden','true');
      control.setAttribute('tabindex','-1');
    });
    const customMetal=root.querySelector('[data-it-metal-picker]');
    const customShape=root.querySelector('[data-it-shape-picker]');
    [customMetal,customShape].forEach(custom=>{
      if(!custom)return;
      custom.hidden=false;
      custom.removeAttribute('aria-hidden');
    });
    const status=root.querySelector('[data-it-filter-status]');
    if(status){
      status.setAttribute('role','status');
      status.setAttribute('aria-live','polite');
      status.setAttribute('aria-atomic','true');
    }
  };
  const detach=()=>{
    if(detachedRoot)return;
    const root=document.querySelector(ROOT_SELECTOR);
    if(!root)return;
    remediateA11y(root);
    placeholder=document.createComment('it-native-settings-route-gate');
    root.replaceWith(placeholder);
    detachedRoot=root;
  };
  const restore=()=>{
    if(!detachedRoot)return document.querySelector(ROOT_SELECTOR);
    if(placeholder?.isConnected)placeholder.replaceWith(detachedRoot);
    const root=detachedRoot;
    detachedRoot=null;
    placeholder=null;
    remediateA11y(root);
    startMediaTruth(root);
    return root;
  };
  const loadPicker=()=>{
    if(pickerLoaded||!pickerSrc)return;
    pickerLoaded=true;
    const script=document.createElement('script');
    script.src=pickerSrc;
    script.dataset.itNativePickerDynamic='true';
    document.head.appendChild(script);
  };
  const syncRoute=()=>{
    if(route()==='nivoda'){
      detach();
      return;
    }
    const root=restore()||document.querySelector(ROOT_SELECTOR);
    remediateA11y(root);
    startMediaTruth(root);
    loadPicker();
  };

  if(route()==='nivoda')detach();
  else{
    const root=document.querySelector(ROOT_SELECTOR);
    remediateA11y(root);
    startMediaTruth(root);
  }
  addEventListener('it:ring-route-change',syncRoute);
})();
